import useAuthenticatedUser from '@UI/Providers/AuthenticatedUserProvider/useAuthenticatedUser';
import type * as React from 'react';
import { Navigate, useLocation } from 'react-router-dom';

type Props = {
	readonly children: React.ReactNode;
};

/**
 * Waits for the session probe to resolve (`sessionStatus !== 'unknown'`) before deciding: at the
 * first render the status is always `'unknown'`, which is not `'authenticated'` and would
 * otherwise bounce a visitor whose session is still being checked back to `/login`.
 * Rendering `null` while waiting is safe here — whoever reaches a session-guarded route already
 * has a session in flight, so the wait is a single, invisible frame.
 */
const RequireSession: React.FC<Props> = ({ children }) => {
	const { sessionStatus } = useAuthenticatedUser();
	const location = useLocation();

	if (sessionStatus === 'unknown') {
		return null;
	}

	if (sessionStatus !== 'authenticated') {
		return <Navigate to="/login" state={{ from: location }} replace />;
	}

	return children;
};

export default RequireSession;
